import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { getProject, Project } from '../services/projects';
import { getAnalyticsSummary, AnalyticsSummary } from '../services/analytics';
import { useAuth } from '../hooks/useAuth';
import { usePlan } from '../hooks/usePlan';
import Layout from '../components/Layout';
import { EmbedSnippet } from '../components/EmbedSnippet';
import { SubscribersList } from '../components/SubscribersList';
import type { Subscriber } from '../services/projects';
import { AnalyticsDashboard } from '../components/AnalyticsDashboard';
import { WebhookManager } from '../components/WebhookManager';
import { SlackIntegration } from '../components/SlackIntegration';
import { ApiKeyManager } from '../components/ApiKeyManager';
import { DomainManager } from '../components/DomainManager';
import { PlanGate } from '../components/PlanGate';
import { friendlyError } from '../utils/errors';
import * as Sentry from '@sentry/react';
import { Skeleton } from '../components/ui/Skeleton';
import { ErrorAlert } from '../components/ui/ErrorAlert';
import { SDK_CDN_URL, API_URL } from '../config';

export default function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const { limits } = usePlan();

  const [project, setProject] = useState<Project | null>(null);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);

  const [loading, setLoading] = useState(true);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !user) return;

    setLoading(true);
    setProjectError(null);
    setAnalyticsError(null);

    Promise.allSettled([getProject(projectId), getAnalyticsSummary(user, projectId)]).then(
      ([projResult, summaryResult]) => {
        if (projResult.status === 'fulfilled') {
          setProject(projResult.value);
        } else {
          console.error('[ProjectDetail] project load error', projResult.reason);
          Sentry.captureException(projResult.reason);
          setProjectError(friendlyError(projResult.reason));
        }

        if (summaryResult.status === 'fulfilled') {
          const summary = summaryResult.value;
          setSubscribers(
            (summary.subscribers ?? []).map((s) => ({
              ...s,
              subscribedAt: s.subscribedAt ? new Date(s.subscribedAt) : null,
            })),
          );
          setAnalytics(summary);
        } else {
          console.error('[ProjectDetail] analytics load error', summaryResult.reason);
          Sentry.captureException(summaryResult.reason);
          setAnalyticsError(friendlyError(summaryResult.reason));
        }

        setLoading(false);
      },
    );
  }, [projectId, user]);

  // A project is "new" when data has loaded and there are no subscribers or events yet
  const isNewProject =
    !loading && subscribers.length === 0 && (analytics?.totalEvents ?? 0) === 0 && !analyticsError;

  const subscriberCount = subscribers.length;
  const atSubscriberLimit = !loading && subscriberCount >= limits.maxSubscribers;
  const nearSubscriberLimit =
    !loading && !atSubscriberLimit && subscriberCount >= limits.maxSubscribers * 0.8;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/"
          className="inline-block text-sm text-brand-500 font-semibold mb-6 hover:underline"
        >
          ← Back to dashboard
        </Link>

        {/* Header — skeleton reserves full height before data loads so the page doesn't shift */}
        <header className="mb-8">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-5 w-1/2" />
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{project?.name ?? ''}</h2>
              {/* Always render the description line to maintain stable height */}
              <p className="text-gray-500 text-base m-0 min-h-[1.5rem]">
                {project?.description ?? ''}
              </p>
            </>
          )}
        </header>

        {projectError && <ErrorAlert label="Could not load project" message={projectError} />}

        {/* Subscriber limit banners */}
        {atSubscriberLimit && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6">
            <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800 m-0">Subscriber limit reached</p>
              <p className="text-xs text-red-700 m-0 mt-0.5">
                Your waitlist has hit the {limits.maxSubscribers}-subscriber limit on the free plan.
                New sign-ups are paused.{' '}
                <Link to="/profile" className="underline font-medium hover:text-red-900">
                  Upgrade to Pro
                </Link>{' '}
                to remove limits.
              </p>
            </div>
          </div>
        )}

        {nearSubscriberLimit && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6">
            <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800 m-0">
                Approaching subscriber limit
              </p>
              <p className="text-xs text-amber-700 m-0 mt-0.5">
                You have {subscriberCount} of {limits.maxSubscribers} subscribers on the free plan.{' '}
                <Link to="/profile" className="underline font-medium hover:text-amber-900">
                  Upgrade to Pro
                </Link>{' '}
                before you hit the limit.
              </p>
            </div>
          </div>
        )}

        {/* 1. Subscribers — the core of a waitlist */}
        <SubscribersList
          subscribers={subscribers}
          loading={loading}
          error={analyticsError}
          isNewProject={isNewProject}
          projectId={projectId ?? ''}
        />

        {/* 2. Analytics & Events */}
        <AnalyticsDashboard summary={analytics} loading={loading} error={analyticsError} />

        {/* 3. Embed Snippet — expanded by default for new projects */}
        <EmbedSnippet
          key={loading ? 'loading' : isNewProject ? 'new' : 'existing'}
          projectId={projectId ?? ''}
          cdnUrl={SDK_CDN_URL}
          apiUrl={API_URL}
          defaultExpanded={isNewProject}
        />

        {/* 4. Webhooks — Pro only */}
        {user && projectId && (
          <PlanGate allowed={limits.webhooks} featureName="Webhooks">
            <WebhookManager user={user} projectId={projectId} />
          </PlanGate>
        )}

        {/* 5. Domain Protection — Pro only */}
        {user && projectId && (
          <PlanGate allowed={limits.domainProtection} featureName="Domain protection">
            <DomainManager
              user={user}
              projectId={projectId}
              initialDomains={project?.allowedDomains ?? []}
            />
          </PlanGate>
        )}

        {/* 6. Slack Integration — Pro only */}
        {user && projectId && (
          <PlanGate allowed={limits.slack} featureName="Slack integration">
            <SlackIntegration user={user} projectId={projectId} />
          </PlanGate>
        )}

        {/* 7. API Keys — Pro only */}
        {projectId && (
          <PlanGate allowed={limits.apiKeys} featureName="API keys">
            <ApiKeyManager projectId={projectId} />
          </PlanGate>
        )}
      </div>
    </Layout>
  );
}
